import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareText, CalendarCheck, Check, ShieldCheck, Accessibility, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { printTools, scrollToId } from "@/lib/scroll";
import { DownloadChoices } from "@/components/DownloadChoices";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { PackPage, packIndex } from "@/components/workbook/PackPage";
import { HealthCheck } from "@/components/tools/HealthCheck";
import { ProvisionReview } from "@/components/tools/ProvisionReview";
import { ParentPartnership } from "@/components/tools/ParentPartnership";
import { EhcpChecklist } from "@/components/tools/EhcpChecklist";
import { WorkloadAudit } from "@/components/tools/WorkloadAudit";
import { GovernorQuestions } from "@/components/tools/GovernorQuestions";
import { ActionPlan } from "@/components/tools/ActionPlan";
import { RegisterReview, AttendanceReview, ActionTracker, ImprovementJournal, SprintPlanner, NinetyDayPlanner, InspectionToAction } from "@/components/workbook/WorkbookTools";
import { LegislationSection, SourcesAppendix, AboutWorkbook, LEGISLATION } from "@/components/workbook/Reference";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SLUG = "sendco-quick-start-workbook";

const WelcomeWb = () => (
  <section id="wb-welcome" aria-labelledby="wb-welcome-title" className="tool-section scroll-mt-36 rounded-[1.75rem] border-2 border-line bg-paper p-5 sm:p-8 lg:p-10">
    <h3 id="wb-welcome-title" className="font-display text-2xl font-medium sm:text-3xl">Welcome</h3>
    <p className="mt-4 max-w-3xl leading-relaxed">This workbook gives SENDCOs, Inclusion Leads, Headteachers, Senior Leaders and Governors one place to reflect on SEND practice, review provision and plan improvement. Use it for:</p>
    <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {["Reflection on what is working and what is not", "Improvement planning with clear actions", "SEND development over 30 and 90 days", "Leadership conversations with your senior team", "Governance support and challenge"].map((t) => (
        <li key={t} className="flex items-start gap-2"><Check size={18} className="mt-0.5 shrink-0 text-brand" aria-hidden />{t}</li>
      ))}
    </ul>
    <p className="mt-5 text-sm text-ink-soft">Your answers save in this browser only. Never enter pupil names or identifiable details. Use references such as Pupil A001.</p>
  </section>
);

export const WB_SECTIONS = [
  { id: "wb-welcome", n: "", title: "Welcome", El: WelcomeWb },
  { id: "health-check", n: "01", title: "SENDCO Health Check", El: HealthCheck },
  { id: "provision-audit", n: "02", title: "SEND Provision Review", El: ProvisionReview },
  { id: "wb-register", n: "03", title: "SEND Register Health Review", El: RegisterReview },
  { id: "wb-attendance", n: "04", title: "Attendance & Inclusion Review", El: AttendanceReview },
  { id: "parent-partnership", n: "05", title: "Parent Partnership Reflection", El: ParentPartnership },
  { id: "ehcp-toolkit", n: "06", title: "EHCP Annual Review Toolkit", El: EhcpChecklist },
  { id: "workload-audit", n: "07", title: "SENDCO Workload Review", El: WorkloadAudit },
  { id: "governor-toolkit", n: "08", title: "Governor SEND Questions", El: GovernorQuestions },
  { id: "action-plan", n: "09", title: "SEND Development Planner", El: ActionPlan },
  { id: "wb-tracker", n: "10", title: "Action Tracker", El: ActionTracker },
  { id: "wb-journal", n: "11", title: "SEND Improvement Journal", El: ImprovementJournal },
  { id: "wb-sprint", n: "12", title: "30-Day SEND Sprint Planner", El: SprintPlanner },
  { id: "wb-ninety", n: "13", title: "90-Day SEND Improvement Planner", El: NinetyDayPlanner },
  { id: "wb-inspection", n: "14", title: "Inspection Findings to Action", El: InspectionToAction },
  { id: "wb-legislation", n: "", title: "Legislative alignment", El: LegislationSection },
  { id: "wb-sources", n: "", title: "Official sources", El: SourcesAppendix },
  { id: "wb-about", n: "", title: "Accessibility, privacy, disclaimer", El: AboutWorkbook },
];
const WB_INDEX = packIndex("Workbook", WB_SECTIONS);

const usePublished = () => {
  const [v, setV] = useState(null);
  useEffect(() => {
    api.get("/resources").then(({ data }) => {
      const wb = Array.isArray(data) ? data.find((r) => r.slug === SLUG) : null;
      setV(wb?.version ?? null);
    }).catch(() => setV(null));
  }, []);
  return v;
};

const FACTS = [
  [Check, "What's included", "14 fill-in sections: health check, provision, register, attendance, parent partnership, EHCP reviews, workload, governor questions, development planning, action tracking, a journal, 30-day sprints, 90-day plans and inspection-to-action."],
  [Accessibility, "Accessibility", "Screen reader and keyboard friendly, high contrast, plain English and print-friendly. Use the controls in the top bar to change text size, font, contrast and motion, or to listen to the page."],
  [ShieldCheck, "Privacy", "No registration or login. Answers stay in your browser. Use pseudonymised references only. Never enter pupil names."],
  [Scale, "Legislative alignment", `Written with reference to ${LEGISLATION.length} pieces of legislation and statutory guidance, including the Children and Families Act 2014 and the SEND Code of Practice.`],
  [MessageSquareText, "Feedback", "Tell us what helped and what to build next, then come back after a month to share the impact."],
];

const Hero = ({ version }) => {
  const pdf = version?.has_file;
  return (
    <section className="no-print mx-auto max-w-[1400px] px-5 pb-16 pt-[150px] sm:px-8 lg:px-10 lg:pt-[178px]" aria-labelledby="wb-title">
      <p className="eyebrow mb-4">Free download · No login · No registration{version?.version ? ` · ${version.version}` : ""}</p>
      <h1 id="wb-title" className="max-w-4xl font-display text-[2.6rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl lg:text-7xl">
        SENDCO Quick Start &amp; <span className="italic text-brand">Improvement Workbook</span>
      </h1>
      <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-soft">Practical SEND reflection, review and improvement planning tools for schools. Download, complete, save, print, revisit.</p>
      <DownloadChoices className="mt-8" testid="wb-download" resource="workbook" label="workbook (PDF)"
        file="sendco-quick-start-improvement-workbook.pdf" href={pdf ? `${BACKEND}/api/resources/${SLUG}/download` : undefined}
        onPrint={() => printTools(null)} onFillOnline={() => scrollToId("wb-welcome")} />
      <div className="mt-4 flex flex-wrap gap-3">
        <Link to="/feedback" className="btn btn-ghost text-base" data-testid="wb-feedback-link"><MessageSquareText size={18} aria-hidden /> Leave feedback</Link>
        <Link to="/impact-review" className="btn btn-ghost text-base" data-testid="wb-impact-link"><CalendarCheck size={18} aria-hidden /> Submit one-month review</Link>
      </div>
      <dl className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {FACTS.map(([Icon, t, d]) => (
          <div key={t} className="rounded-2xl border-2 border-line bg-surface p-5">
            <dt className="flex items-center gap-2 font-bold"><Icon size={18} className="text-brand" aria-hidden />{t}</dt>
            <dd className="mt-2 text-sm leading-relaxed text-ink-soft">{d}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default function WorkbookPage() {
  const version = usePublished();
  return (
    <>
      <TopBar minimal index={WB_INDEX} />
      <main id="main" tabIndex={-1} className="outline-none">
        <Hero version={version} />
        <PackPage packId="workbook" label="workbook" sections={WB_SECTIONS}
          cover={{ eyebrow: `SENDCO QUICK START & IMPROVEMENT WORKBOOK ${version?.version || "v1.0"}`, title: "Quick Start & Improvement Workbook",
            subtitle: "Practical SEND Reflection, Review and Improvement Planning Tools for Schools",
            lines: ["Supporting: SENDCOs · Inclusion Leads · Headteachers · Senior Leaders · Governors", "Free Download · No Registration Required", "Download • Complete • Save • Print • Revisit"] }} />
      </main>
      <Footer />
    </>
  );
}
